import React, { useRef, useState } from 'react';
import { saveAs } from 'file-saver';

import { fetch_payout_details_export } from '../api';
import { api_error } from '../format';
import { export_unavailable_count, export_unavailable_text, review_error } from './adapters';
import { CSV_ROW_CAP } from '../../../util_components/csv_export/collect_pages';
import '../../../util_components/csv_export/csv_export.css';

// "Download with full details": the server-built, audited pending CSV with full account number, PAN and GSTIN.
const FULL_EXPORT_FAILED_TEXT = 'Could not download the full payment details. Try again.';
const FULL_EXPORT_NOTE = 'Full bank and tax details. This download is recorded.';
const FULL_EXPORT_FALLBACK_NAME = 'teacher_payment_details_pending.csv';
const CODE_EXPORT_TOO_LARGE = 'PAYMENT_DETAILS_EXPORT_TOO_LARGE';
// The server caps this file at the masked CSV's cap (export_max_rows == CSV_ROW_CAP), so both hold the same teachers.
const TOO_LARGE_TEXT = `More than ${CSV_ROW_CAP.toLocaleString('en-IN')} payment details are waiting, which is more than one download holds. Review some first, then try again.`;

// The saved file's UNAVAILABLE warning ('' when none); an unreadable blob skips it (the file already saved).
const unavailable_warning = (blob) =>
  Promise.resolve()
    .then(() => blob.text())
    .then(
      (text) => export_unavailable_text(export_unavailable_count(text)),
      () => ''
    );

function FullDetailsDownload({ on_error, on_notify }) {
  const [busy, setBusy] = useState(false);
  const [warning, setWarning] = useState('');
  const busy_ref = useRef(false);

  const run = () => {
    if (busy_ref.current) return;
    busy_ref.current = true;
    setBusy(true);
    setWarning('');
    fetch_payout_details_export()
      .then(({ blob, file_name }) => {
        saveAs(blob, file_name || FULL_EXPORT_FALLBACK_NAME);
        // toast now, plus an inline line that outlasts it
        unavailable_warning(blob).then((warn) => {
          if (!warn) return;
          setWarning(warn);
          on_notify('error', warn);
        });
      })
      .catch((e) => {
        const too_large = api_error(e).code === CODE_EXPORT_TOO_LARGE;
        const v = review_error(e, too_large ? TOO_LARGE_TEXT : FULL_EXPORT_FAILED_TEXT);
        if (v.kind === 'auth') on_error(e, FULL_EXPORT_FAILED_TEXT);
        else on_notify('error', v.text);
      })
      .finally(() => {
        busy_ref.current = false;
        setBusy(false);
      });
  };

  // Fragment: the parent grid puts the button beside Download CSV and the note on the row below.
  return (
    <>
      <span className="csv-dl tp-pd-full-dl">
        <button
          type="button"
          className="csv-dl-btn"
          onClick={run}
          aria-disabled={busy ? 'true' : undefined}
          aria-busy={busy ? 'true' : undefined}
        >
          {busy ? 'Preparing…' : '↓ Download with full details'}
        </button>
      </span>
      <span className="csv-dl-note tp-pd-full-dl-note">{FULL_EXPORT_NOTE}</span>
      {warning ? (
        <p className="tp-neg tp-pd-full-dl-warn" role="alert">
          {warning}
        </p>
      ) : null}
    </>
  );
}

export default FullDetailsDownload;
